import { supabase } from '../supabase';
import { cache } from './cache.service';
import { calculateEquityScore } from '@/utils/equityCalculator';
import type { 
  Customer, 
  Vehicle,
  EquityScore, 
  Valuation,
  Notification,
  CreditInfo,
  LoanInfo 
} from '@/types/customer';

interface CreditCheckResponse {
  status: 'success' | 'error' | 'pending' | 'skipped' | 'exists';
  message: string;
  credit_score?: number;
  credit_freeze?: boolean;
  credit_info?: Array<{
    credit_score: number;
    credit_freeze: boolean;
    credit_error: boolean;
    no_hit: boolean;
    created_at: string;
  }>;
  loan_info?: WebhookLoanInfo[];
}

interface WebhookLoanInfo {
  balance: number;
  monthly_payment: number;
  acct_type: 'auto' | 'auto lease';
  est_apr?: string;
}

interface VehicleSearchResult {
  id: number;
  year: string;
  make: string;
  model: string;
  trim: string;
  vin: string;
  plate: string;
  customer_data: {
    id: number;
    created_at: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    mileage: number | null;
    status: boolean;
    engaged: boolean;
    valuations?: Array<{
      low: number;
      target: number;
      high: number;
    }>;
    loan_info?: Array<{
      loan_balance: number;
      payment: number;
    }>;
    credit_info?: Array<{
      credit_score: number;
    }>;
  };
}

interface LoanResponse {
  credit_score: string;
  balance: number;
  monthly_payment: number;
  acct_type: 'auto' | 'auto lease';
}

interface FinancialInfo {
  loan_balance: number;
  payment: number;
  acct_type: 'auto' | 'auto lease';
}

export interface CustomerResponse {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  credit_score?: number;
  credit_freeze?: boolean;
  loan_info?: Array<FinancialInfo>;
  valuation?: {
    low: number;
    target: number;
    high: number;
  };
  vehicle?: {
    year: string;
    make: string;
    model: string;
    trim?: string;
    engine?: string;
    drivetrain?: string;
    body?: string;
    fuel_type?: string;
  };
  equity_score?: number;
  equity_ratio?: number;
}

// Cache system for sold car data to limit API calls to once per day
const soldCarDataCache = {
  // Get cached data if it exists and is less than 24 hours old
  get(rooftopId: string) {
    try {
      const cacheKey = `soldCarData_${rooftopId}`;
      const cachedString = localStorage.getItem(cacheKey);
      
      if (!cachedString) return null;
      
      const entry = JSON.parse(cachedString);
      
      // Check if cache is still valid (less than 24 hours old)
      const now = Date.now();
      const cacheAge = now - entry.timestamp;
      const oneDayMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      
      if (cacheAge > oneDayMs) {
        // Cache expired
        console.log(`Cache for rooftop ${rooftopId} expired (${Math.round(cacheAge/3600000)} hours old)`);
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      console.log(`Using cached data for rooftop ${rooftopId} (${Math.round(cacheAge/60000)} minutes old)`);
      return entry.data;
    } catch (error) {
      console.error('Error accessing cache:', error);
      return null;
    }
  },
  
  // Store data in cache with current timestamp
  set(rooftopId: string, data: any[]) {
    try {
      const cacheKey = `soldCarData_${rooftopId}`;
      const cacheEntry = {
        timestamp: Date.now(),
        data
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      console.log(`Cached data for rooftop ${rooftopId} in localStorage`);
    } catch (error) {
      console.error('Error storing cache:', error);
    }
  },
  
  // Delete data from cache
  delete(rooftopId: string) {
    try {
      const cacheKey = `soldCarData_${rooftopId}`;
      localStorage.removeItem(cacheKey);
      console.log(`Deleted cached data for rooftop ${rooftopId}`);
    } catch (error) {
      console.error('Error deleting cache:', error);
    }
  }
};

// Cache system for service appointment data
const serviceAppointmentCache = {
  // Get cached data if it exists and is less than 24 hours old
  get(cacheKey: string) {
    try {
      const cachedString = localStorage.getItem(cacheKey);
      
      if (!cachedString) return null;
      
      const entry = JSON.parse(cachedString);
      
      // Check if cache is still valid (less than 24 hours old)
      const now = Date.now();
      const cacheAge = now - entry.timestamp;
      const oneDayMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      
      if (cacheAge > oneDayMs) {
        // Cache expired
        console.log(`Cache for ${cacheKey} expired (${Math.round(cacheAge/3600000)} hours old)`);
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      console.log(`Using cached data for ${cacheKey} (${Math.round(cacheAge/60000)} minutes old)`);
      return entry.data;
    } catch (error) {
      console.error('Error accessing cache:', error);
      return null;
    }
  },
  
  // Set cache data with current timestamp
  set(cacheKey: string, data: any) {
    try {
      const entry = {
        timestamp: Date.now(),
        data
      };
      localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (error) {
      console.error('Error setting cache:', error);
    }
  },
  
  // Delete a cache entry
  delete(cacheKey: string) {
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error deleting cache:', error);
    }
  },
  
  // Clear all service appointment caches
  clearAllServiceCaches() {
    try {
      // Get all keys from localStorage
      const keys = Object.keys(localStorage);
      
      // Find and remove all service appointment related caches
      const serviceCacheKeys = keys.filter(key => 
        key.startsWith('service_appointments_') || 
        key.startsWith('verified_appointments_')
      );
      
      console.log(`Clearing ${serviceCacheKeys.length} service appointment caches`);
      
      serviceCacheKeys.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing service appointment caches:', error);
    }
  }
};

/**
 * Thisis for supaabse not pipedream to only let allowed emails into the app 
 */
export async function addAllowedEmail(email: string) {
  const response = await fetch(import.meta.env.VITE_PIPEDREAM_WEBHOOK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_API_BEARER_TOKEN}`,
      'X-Source': 'grip-frontend'
    },
    body: JSON.stringify({ email, action: 'add_allowed_email' })
  });

  if (!response.ok) {
    throw new Error('Failed to add email to allowed list');
  }

  return response.json();
}

export const checkAllowedEmail = async (email: string): Promise<boolean> => {
  // Implement your email verification logic here
  // For example, checking against a whitelist or database
  return true // Temporary return until you implement the actual check
}

const trackCreditPull = async (rooftopId: string, customerId: number, userId: string) => {
  try {
    // First, count existing pulls for this month
    const currentDate = new Date();
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    const { count, error: countError } = await supabase
      .from('credit_pulls')
      .select('*', { count: 'exact', head: true })
      .eq('rooftop_id', rooftopId)
      .gte('created_at', firstDayOfMonth.toISOString())
      .lte('created_at', lastDayOfMonth.toISOString());

    if (countError) throw countError;

    const currentPulls = count || 0;
    const isOverage = currentPulls >= 250;
    const overageCost = isOverage ? 2.50 : 0;

    // Insert the new pull with overage information
    const { data, error } = await supabase
      .from('credit_pulls')
      .insert([{
        rooftop_id: rooftopId,
        customer_id: customerId,
        user_id: userId,
        created_at: new Date().toISOString(),
        cost: 0,
        is_overage: isOverage,
        overage_cost: overageCost
      }])
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error tracking credit pull:', error);
    throw error;
  }
};

export const api = {
  async getCustomersList(rooftopId: string) {
    try {
      console.log('Fetching customers list...');
      
      const { data, error } = await supabase
        .from('customer_data')
        .select(`
          *,
          vehicles (
            id,
            year,
            make,
            model
          ),
          valuations (
            low,
            target,
            high
          ),
          credit_info (
            credit_score,
            credit_freeze,
            created_at
          ),
          loan_info (
            loan_balance,
            payment,
            created_at,
            acct_type
          )
        `)
        .eq('rooftop_id', rooftopId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Raw customer data:', data);

      const transformedData = data?.map(customer => ({
        id: customer.id,
        created_at: customer.created_at,
        first_name: customer.first_name || 'Unknown',
        last_name: customer.last_name || '',
        mileage: customer.mileage,
        phone: customer.phone,
        email: customer.email,
        status: customer.status,
        vehicle: customer.vehicles?.[0] || null,
        valuation: customer.valuations?.[0] || null,
        credit_score: customer.credit_info?.[0]?.credit_score || null,
        credit_freeze: customer.credit_info?.[0]?.credit_freeze || false,
        loan_info: customer.loan_info || [],
        engaged: customer.engaged || false,
        appt_time: customer.appt_time,
        checkin_time: customer.created_at,
        appt_date: customer.appt_date
      })) || [];

      console.log('Transformed customer data:', transformedData);
      return transformedData;

    } catch (error) {
      console.error('Error in getCustomersList:', error);
      return [];
    }
  },

  async getCustomerDetails(customerId: number, rooftopId: string) {
    try {
      console.log('Fetching customer details for ID:', customerId);
      
      const { data, error } = await supabase
        .from('customer_data')
        .select(`
          *,
          vehicles (
            id,
            year,
            make,
            model,
            trim,
            engine,
            drivetrain,
            body,
            fuel_type
          ),
          valuations (
            low,
            target,
            high
          ),
          credit_info (
            credit_score,
            credit_freeze,
            credit_error,
            no_hit,
            created_at
          ),
          loan_info (
            loan_balance,
            payment,
            created_at,
            acct_type,
            primary,
            id
          ),
          equity_scores (
            id,
            score,
            created_at
          )
        `)
        .eq('id', customerId)
        .eq('rooftop_id', rooftopId)
        .single();

      if (error) throw error;
      
      console.log('Raw customer details:', data);
      return data;

    } catch (error) {
      console.error('Error in getCustomerDetails:', error);
      throw error;
    }
  },

  async runCreditCheck(customerId: number, rooftopId: string): Promise<CreditCheckResponse> {
    try {
      console.log('Starting credit check for customer:', customerId);
      
      // Get customer data
      const { data: customerData, error: customerError } = await supabase
        .from('customer_data')
        .select('*')
        .eq('id', customerId)
        .single();

      if (customerError) {
        console.error('Error fetching customer data:', customerError);
        throw new Error('Failed to fetch customer data');
      }

      if (!customerData) {
        throw new Error('Customer not found');
      }

      if (!customerData.opt_in) {
        console.log('Customer has not opted in, skipping credit check');
        return { status: 'skipped', message: 'Customer has not opted in' };
      }

      // Get current user ID for tracking
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Track the credit pull
      const { isOverage, cost } = await trackCreditPull(rooftopId, customerId, user.id);

      // If this is an overage pull, we could add additional logging or notifications here
      if (isOverage) {
        console.log(`Credit pull overage for dealer ${rooftopId}. Cost: $${cost}`);
      }

      // Check if credit info already exists
      const { data: existingCreditInfo, error: creditError } = await supabase
        .from('credit_info')
        .select('id, processing')
        .eq('customer_id', customerId)
        .maybeSingle();

      if (creditError && creditError.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error checking existing credit info:', creditError);
        throw new Error('Failed to check existing credit info');
      }

      if (existingCreditInfo?.processing) {
        console.log('Credit check already in progress for customer:', customerId);
        return { status: 'skipped', message: 'Credit check is already in progress' };
      }

      if (existingCreditInfo) {
        console.log('Credit info already exists for customer:', customerId);
        return { status: 'exists', message: 'Credit info already exists' };
      }

      // Create initial credit info record with processing flag
      const { error: processingError } = await supabase
        .from('credit_info')
        .insert({
          customer_id: customerId,
          processing: true,
          created_at: new Date().toISOString()
        });

      if (processingError) {
        console.error('Error creating processing record:', processingError);
        throw new Error('Failed to create processing record');
      }

      // Prepare request body
      const requestBody = {
        customer_id: customerId,
        rooftop_id: rooftopId,
        first_name: customerData.first_name,
        last_name: customerData.last_name,
        email: customerData.email,
        phone: customerData.phone,
        address: customerData.address,
        city: customerData.city,
        state: customerData.state,
        zip: customerData.zip,
        ssn: customerData.ssn,
        dob: customerData.dob
      };

      console.log('Sending request to credit check webhook:', requestBody);

      // Make request to webhook
      // Use test webhook for test rooftop_id
      const webhookUrl =
        rooftopId === 'DVD00001'
          ? import.meta.env.VITE_TEST_PIPEDREAM_WEBHOOK_URL
          : import.meta.env.VITE_PIPEDREAM_WEBHOOK_URL;
      if (!webhookUrl) {
        throw new Error('Credit check webhook URL is not configured');
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_API_BEARER_TOKEN}`,
          'X-Source': 'credit-check-init'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Webhook request failed with status ${response.status}`);
      }

      const responseText = await response.text();
      console.log('Raw response from webhook:', responseText);

      // Try to parse as JSON first
      let data;
      try {
        // First try to parse as a simple number (credit score)
        const simpleScore = parseInt(responseText);
        if (!isNaN(simpleScore)) {
          console.log('Received simple credit score:', simpleScore);
          
          // Update existing credit info record
          const { error: creditError } = await supabase
            .from('credit_info')
            .update({
              credit_score: simpleScore,
              credit_error: false,
              no_hit: false,
              processing: false
            })
            .eq('customer_id', customerId);

          if (creditError) {
            console.error('Error updating credit info:', creditError);
            throw new Error('Failed to update credit info');
          }

          // Fetch the updated credit info
          const { data: updatedCreditInfo } = await supabase
            .from('credit_info')
            .select('*')
            .eq('customer_id', customerId)
            .single();

          return { 
            status: 'success', 
            message: 'Credit check completed successfully',
            credit_score: simpleScore,
            credit_info: updatedCreditInfo ? [updatedCreditInfo] : []
          };
        }

        // If not a simple number, try parsing as JSON
        data = JSON.parse(responseText);
        console.log('Parsed webhook response:', data);
        
        // Check if it's an array of loan data
        if (Array.isArray(data) && data.length > 0) {
          console.log('Found loan data array with length:', data.length);
          // Get credit score from first record
          const creditScore = parseInt(data[0].credit_score);
          
          // Update existing credit info record instead of creating a new one
          const { error: creditError } = await supabase
            .from('credit_info')
            .update({
              credit_score: creditScore,
              credit_error: false,
              no_hit: false,
              processing: false
            })
            .eq('customer_id', customerId);

          if (creditError) {
            console.error('Error updating credit info:', creditError);
            throw new Error('Failed to update credit info');
          }

          // Save loan info for each record
          for (const loan of data) {
            console.log('Saving loan record:', loan);
            const { error: loanError } = await supabase
              .from('loan_info')
              .insert({
                customer_id: customerId,
                rooftop_id: rooftopId.toString(),
                loan_balance: loan.balance,
                payment: loan.monthly_payment,
                acct_type: loan.acct_type,
                current_rate: loan.est_apr ? loan.est_apr.replace('%', '') : null,
                primary: false
              });

            if (loanError) {
              console.error('Error saving loan info:', loanError);
              throw new Error('Failed to save loan info');
            }
            console.log('Successfully saved loan record');
          }

          // Fetch the updated credit info to ensure we have the latest data
          const { data: updatedCreditInfo } = await supabase
            .from('credit_info')
            .select('*')
            .eq('customer_id', customerId)
            .single();

          // Fetch all loan info for this customer
          const { data: allLoanInfo } = await supabase
            .from('loan_info')
            .select('*')
            .eq('customer_id', customerId)
            .eq('rooftop_id', rooftopId.toString());

          console.log('Fetched all loan info:', allLoanInfo);

          return { 
            status: 'success', 
            message: 'Credit check completed and data saved successfully',
            credit_score: creditScore,
            credit_info: updatedCreditInfo ? [updatedCreditInfo] : [],
            loan_info: allLoanInfo || []
          };
        }

        // If it's an error response
        if (data.error) {
          return { status: 'error', message: data.error };
        }

        // If it's not in the expected format
        throw new Error('Unexpected response format from credit check');
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        console.log('Raw response:', responseText);
        throw new Error('Failed to parse credit check response: ' + responseText);
      }
    } catch (error) {
      console.error('Error in runCreditCheck:', error);
      throw error;
    }
  },

  async getNotifications(rooftopId: string) {
    try {
      const { data: notifications, error } = await supabase
        .from('notifications')
        .select(`
          *,
          customer_data (first_name, last_name)
        `)
        .eq('rooftop_id', rooftopId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return notifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  },

  async markNotificationAsRead(notificationId: number) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  async customerCheckIn(customerData: {
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    city: string;
    state: string;
    plate: string;
    mileage: string | number;
    address: string;
    zip: string;
    rooftop_id: string;
    engaged: boolean;
  }) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        await supabase.auth.refreshSession();
      }

      console.log('Starting customer check-in process:', customerData);

      // 1. First save to Supabase
      const { data: newCustomer, error: insertError } = await supabase
        .from('customer_data')
        .insert([
          {
            ...customerData,
            created_at: new Date().toISOString(),
            vehicle_data_status: 'pending',
            engaged: true // Use engaged instead of checked_in
          }
        ])
        .select()
        .single();

      if (insertError) throw insertError;
      
      if (!newCustomer) {
        throw new Error("could not insert customer data")
      }
      
      const {
        id: customerId,
        plate,
        state,
        mileage
      } = newCustomer
      
      try {
        const { data: _tradePendingResponse, error: tradePendingError } = await supabase
          .functions
          .invoke(
            'trade-pending',
            {
              body: {
                customerId,
                plate,
                state,
                mileage
              }
            }
          )
          
        if (tradePendingError) {
          console.warn('Trade Pending function failed but customer data was saved:', tradePendingError)
        } else {
          console.log('Trade Pending function completed successfully')
        }
      } catch (tradePendingCatchError) {
        // Handle CORS errors gracefully
        console.warn('Trade Pending function error caught (likely CORS in development):', tradePendingCatchError)
        console.log('This error can be ignored in development environment')
      }

      return {
        success: true,
        customer: newCustomer
      };

    } catch (error) {
      console.error('Error in customerCheckIn:', error);
      throw error;
    }
  },

  async searchCustomersByName(query: string, rooftopId: string) {
    try {
      const searchTerm = query.trim().toLowerCase();
      
      if (!searchTerm) return [];

      // Split the search term into parts for full name search
      const searchTerms = searchTerm.split(' ').filter(term => term.length > 0);

      // First search for customers by name
      const customerSearch = await supabase
        .from('customer_data')
        .select(`
          id,
          created_at,
          first_name,
          last_name,
          email,
          phone,
          mileage,
          status,
          engaged,
          vehicles (
            id,
            year,
            make,
            model,
            trim,
            vin,
            plate
          ),
          valuations (
            low,
            target,
            high
          ),
          loan_info (
            loan_balance,
            payment,
            acct_type
          ),
          credit_info (
            credit_score
          )
        `)
        .eq('rooftop_id', rooftopId)
        .or(
          searchTerms.length > 1
            ? `first_name.ilike.%${searchTerms[0]}%,last_name.ilike.%${searchTerms[1]}%`
            : `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`
        )
        .order('created_at', { ascending: false });

      // Then search for vehicles
      const vehicleSearch = await supabase
        .from('vehicles')
        .select(`
          id,
          year,
          make,
          model,
          trim,
          vin,
          plate,
          customer_data!inner (
            id,
            created_at,
            first_name,
            last_name,
            email,
            phone,
            mileage,
            status,
            engaged,
            rooftop_id,
            valuations (
              low,
              target,
              high
            ),
            loan_info (
              loan_balance,
              payment,
              acct_type
            ),
            credit_info (
              credit_score
            )
          )
        `)
        .eq('rooftop_id', rooftopId)
        .eq('customer_data.rooftop_id', rooftopId)
        .or(`make.ilike.%${searchTerm}%,model.ilike.%${searchTerm}%,vin.ilike.%${searchTerm}%,plate.ilike.%${searchTerm}%`);

      if (customerSearch.error) throw customerSearch.error;
      if (vehicleSearch.error) throw vehicleSearch.error;

      // Format customer results
      const customerResults = (customerSearch.data || []).map(customer => ({
        id: customer.id,
        created_at: customer.created_at,
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        mileage: customer.mileage,
        vehicle: customer.vehicles?.[0] || null,
        valuation: customer.valuations?.[0] || null,
        loan_info: customer.loan_info || [],
        credit_score: customer.credit_info?.[0]?.credit_score,
        status: customer.status || false,
        engaged: customer.engaged || false
      }));

      // Format vehicle results
      const vehicleResults = ((vehicleSearch.data as unknown) as VehicleSearchResult[]).map(vehicle => ({
        id: vehicle.customer_data.id,
        created_at: vehicle.customer_data.created_at,
        first_name: vehicle.customer_data.first_name || '',
        last_name: vehicle.customer_data.last_name || '',
        email: vehicle.customer_data.email || '',
        phone: vehicle.customer_data.phone || '',
        mileage: vehicle.customer_data.mileage,
        status: vehicle.customer_data.status || false,
        engaged: vehicle.customer_data.engaged || false,
        vehicle: {
          id: vehicle.id,
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          trim: vehicle.trim,
          vin: vehicle.vin,
          plate: vehicle.plate
        },
        valuation: vehicle.customer_data.valuations?.[0] || null,
        loan_info: vehicle.customer_data.loan_info || [],
        credit_score: vehicle.customer_data.credit_info?.[0]?.credit_score
      }));

      // Combine results and remove duplicates based on customer ID
      const allResults = [...customerResults, ...vehicleResults];
      const uniqueResults = Array.from(new Map(allResults.map(item => [item.id, item])).values());

      return uniqueResults.slice(0, 50);

    } catch (error) {
      console.error('Error searching customers by name:', error);
      throw error;
    }
  },

  async searchVehiclesByAny(query: string, rooftopId: string) {
    try {
      const searchTerm = query.trim().toLowerCase();
      
      if (!searchTerm) return [];

      const { data, error } = await supabase
        .from('vehicles')
        .select(`
          id,
          year,
          make,
          model,
          trim,
          vin,
          plate
        `)
        .eq('rooftop_id', rooftopId)
        .or(`make.ilike.%${searchTerm}%,model.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      return data?.map(vehicle => ({
        id: vehicle.id,
        year: vehicle.year || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        trim: vehicle.trim || '',
        vin: vehicle.vin || '',
        plate: vehicle.plate || ''
      })) || [];

    } catch (error) {
      console.error('Error searching vehicles:', error);
      throw error;
    }
  },
  async GetCustomerDataFromId(customer_id: Number) {
    // Get loan info and equity scores
    const { data: loanData, error: loanError } = await supabase
      .from('loan_info')
      .select()
      .eq('customer_id', customer_id);
      
    // Get customer details with equity scores
    const { data: customerData, error: customerError } = await supabase
      .from('customer_data')
      .select(`
        id,
        equity_scores (
          id,
          score,
          created_at
        )
      `)
      .eq('id', customer_id)
      .single();
      
    console.log('Customer data with equity scores:', customerData);
      
    // Add equity_scores to loan data
    const enrichedData = loanData?.map(loan => ({
      ...loan,
      equity_scores: customerData?.equity_scores || []
    }));
      
    return enrichedData as Array<{
      loan_balance: number;
      payment: number;
      equity_scores?: Array<{
        id: number;
        score: number;
        created_at: string;
      }>;
    }>;
  },
  async updateCustomerStatus(customerId: number, statusUpdate: { engaged: boolean }) {
    try {
      const currentTime = new Date().toISOString();

      console.log(`Updating customer status for ID: ${customerId} with data:`, statusUpdate);

      // Ensure checked_in is also updated to match engaged status
      const { error } = await supabase
        .from('customer_data')
        .update({
          ...statusUpdate,
          checked_in: statusUpdate.engaged, // Always set checked_in to match engaged for trigger compatibility
          created_at: currentTime
        })
        .eq('id', customerId);

      if (error) {
        console.error('Error updating customer status:', error);
        throw error;
      }

      console.log('Customer status updated successfully');
      return { success: true };
    } catch (error) {
      console.error('Error in updateCustomerStatus:', error);
      return { success: false, error };
    }
  },
  async updateCustomerReviewedStatus(customerId: number, reviewed: boolean) {
    try {
      console.log(`Updating reviewed status for customer ID: ${customerId} to:`, reviewed);

      const { error } = await supabase
        .from('customer_data')
        .update({ reviewed })
        .eq('id', customerId);

      if (error) {
        console.error('Error updating customer reviewed status:', error);
        throw error;
      }

      console.log('Customer reviewed status updated successfully');
      return { success: true };
    } catch (error) {
      console.error('Error in updateCustomerReviewedStatus:', error);
      return { success: false, error };
    }
  },
  async updateCustomerEngagedStatus(customerId: number, engaged: boolean) {
    try {
      console.log(`Updating engaged status for customer ID: ${customerId} to:`, engaged);

      // Only update the engaged field - don't try to update checked_in since it doesn't exist
      const { error } = await supabase
        .from('customer_data')
        .update({ 
          engaged: engaged
        })
        .eq('id', customerId);

      if (error) {
        console.error('Error updating customer engaged status:', error);
        throw error;
      }

      console.log('Customer engaged status updated successfully');
      return { success: true };
    } catch (error) {
      console.error('Error in updateCustomerEngagedStatus:', error);
      // Even if the API call fails, the UI will show as if it succeeded
      // The database inconsistency will be fixed next time the page loads
      return { success: false, error };
    }
  },
  async getSoldCarData(rooftopId: string, forceRefresh: boolean = false) {
    try {
      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedData = soldCarDataCache.get(rooftopId);
        if (cachedData) {
          return { data: cachedData, error: null };
        }
      }

      // Query the sold_cars table directly
      const { data, error } = await supabase
        .from('sold_cars')
        .select('*')
        .eq('rooftop_id', rooftopId)
        .not('salesperson', 'is', null)  // Exclude null salesperson
        .not('salesperson', 'eq', '')    // Exclude empty salesperson
        .order('sold_date', { ascending: false });

      if (error?.code === '42P01') {
        // Table doesn't exist, return empty data with a specific error code
        console.log('Sold cars table not found, returning empty data');
        return { 
          data: [], 
          error: {
            code: 'TABLE_NOT_FOUND',
            message: 'Sales data table not yet set up',
            details: 'The sold_cars table has not been created yet. This is expected for new dealers.'
          }
        };
      }

      if (error) throw error;

      // Cache the data
      soldCarDataCache.set(rooftopId, data);
      return { data, error: null };

    } catch (error) {
      console.error('Error in getSoldCarData:', error);
      return { 
        data: [], 
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred',
          details: error
        }
      };
    }
  },
  async getServiceAppointments(rooftopId: string, startDate: string, endDate: string) {
    try {
      const cacheKey = `service_appointments_${rooftopId}_${startDate}_${endDate}`;
      const cachedData = serviceAppointmentCache.get(cacheKey);
      if (cachedData) {
        return { data: cachedData, error: null };
      }

      const { data, error } = await supabase
        .from('service_appointments')
        .select('*')
        .eq('rooftop_id', rooftopId)
        .gte('appointment_date', startDate)
        .lte('appointment_date', endDate)
        .order('appointment_date', { ascending: true });

      if (error) throw error;

      serviceAppointmentCache.set(cacheKey, data);
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching service appointments:', error);
      return { data: null, error };
    }
  },
  async verifyServiceAppointments(rooftopId: string, startDate: string, endDate: string) {
    try {
      const cacheKey = `verified_appointments_${rooftopId}_${startDate}_${endDate}`;
      const cachedData = serviceAppointmentCache.get(cacheKey);
      if (cachedData) {
        return { data: cachedData, error: null };
      }

      const { data, error } = await supabase
        .from('service_appointments')
        .select(`
          *,
          service_data (
            id,
            DV_customer_number,
            open_date
          )
        `)
        .eq('rooftop_id', rooftopId)
        .gte('appointment_date', startDate)
        .lte('appointment_date', endDate)
        .order('appointment_date', { ascending: true });

      if (error) throw error;

      // Process the data to add verification status
      const processedData = {
        appointments: data?.map(appointment => ({
          ...appointment,
          is_verified: Boolean(appointment.service_data?.length)
        })) || [],
        metrics: {
          total: data?.length || 0,
          verified: data?.filter(appointment => appointment.service_data?.length).length || 0,
          rate: data?.length ? 
            Math.round((data.filter(appointment => appointment.service_data?.length).length / data.length) * 100) : 0
        }
      };

      serviceAppointmentCache.set(cacheKey, processedData);
      return { data: processedData, error: null };
    } catch (error) {
      console.error('Error verifying service appointments:', error);
      return { data: null, error };
    }
  },
  async getServiceData(customerId: number, rooftopId: string) {
    try {
      console.log('Fetching service data for:', { customerId, rooftopId });
      
      // First get the customer's name
      const { data: customerData, error: customerError } = await supabase
        .from('customer_data')
        .select('first_name, last_name')
        .eq('id', customerId)
        .single();

      if (customerError) {
        console.error('Error fetching customer name:', customerError);
        throw customerError;
      }

      console.log('Customer name:', customerData);

      if (!customerData?.first_name || !customerData?.last_name) {
        console.log('No customer name found');
        return null;
      }

      // Now query service_data with the customer's name using case-insensitive search
      const { data, error } = await supabase
        .from('service_data')
        .select('*')
        .ilike('first_name', customerData.first_name) // Case-insensitive match
        .ilike('last_name', customerData.last_name)   // Case-insensitive match
        .eq('rooftop_id', rooftopId)
        .order('open_date', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching service data:', error);
        throw error;
      }
      
      console.log('Service data response:', data);
      return data?.[0] || null;
    } catch (error) {
      console.error('Error in getServiceData:', error);
      throw error;
    }
  },
};

// Helper function to normalize phone numbers for comparison
function normalizePhone(phone: string): string {
  if (!phone) return '';
  // Remove all non-digit characters
  return phone.replace(/\D/g, '').slice(-10);
}

// Helper to get first day of 2 months ago
function getStartOfMonth() {
  const date = new Date();
  // Go back 2 months instead of the current month
  date.setMonth(date.getMonth() - 2);
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString();
}
